<script>
import FolderNode from "@/components/FolderNode.vue";
import WebmailCompose from "@/components/WebmailCompose.vue";
import DELETE_MAIL from '@/gql/mutations/deleteMail.graphql'
import MOVE_MAIL from '@/gql/mutations/moveMail.graphql'
import WEBMAIL_MAIL from '@/gql/queries/webmailMail.graphql'
import WEBMAIL_MAIL_BOX from '@/gql/queries/webmailMailBox.graphql'
import USER_MAIL_CONFIG_LIST from '@/gql/queries/userMailConfigList.graphql'
import WEBMAIL_FOLDERS from '@/gql/queries/webmailFolders.graphql'
import ESTABLISHMENT_SET from '@/gql/queries/establishmentSet.graphql'
import ASSISTANT from '@/gql/queries/assistants.graphql'
import {gqlQuery, gqlMutation} from '@/gqlClient.js'
import settings from "@/settings";

export default {
  name: "WebmailDetail",
  components: {FolderNode, WebmailCompose},

  data() {
    return {
      config: null,
      folders: [],
      selectedFolder: null,
      mails: [],
      selectedMailId: null,

      loading: true,
      loadingMails: false,
      loadingMailView: false,

      mail: null,
      showReply: false,
      writeEmail: false,

      assistantset: [],
      composeDirty: false,
      pendingNavigation: null,

      // Sélection multiple
      selectedMails: [],
      hoveredMailUid: null,
      replyContext: '',

      openFolders: {},

      // Pour le dropdown de déplacement
      showMoveDropdown: false,

      confirmModal: {
        visible: false,
        message: '',
        resolve: null,
      },
    };
  },

  computed: {
    treeFolders() {
      const root = [];

      this.folders
          .filter(f => !f.flags.includes("\\Noselect"))
          .forEach(f => {
            const separator = f.name.includes("/") ? "/" : ".";
            const parts = f.name.split(separator);
            let current = root;

            parts.forEach((part, index) => {
              const displayName = (index === 0 && part.toLowerCase() === "inbox") ? "Boite de réception" : part;

              let existing = current.find(x => x.name === displayName);
              let fullName = parts.slice(0, index + 1).join(separator);
              if (!existing) {
                existing = {
                  name: displayName,
                  fullName: fullName,
                  children: [],
                  level: index,
                  open: !!this.openFolders[fullName],
                };
                current.push(existing);
              }

              current = existing.children;
            });
          });

      const sortTree = (nodes) => {
        nodes.sort((a, b) => {
          if (a.name === "Boite de réception") return -1;
          if (b.name === "Boite de réception") return 1;
          return a.name.localeCompare(b.name);
        });
        nodes.forEach(node => sortTree(node.children));
      };

      sortTree(root);
      return root;
    },

    availableFoldersForMove() {
      return this.folders
          .filter(f =>
              !f.flags.includes("\\Noselect") &&
              f.name !== this.selectedFolder &&
              !f.isDraftFolder
          );
    },

    selectedCount() {
      return this.selectedMails.length;
    },

    flatFoldersForMove() {
      const allowedNames = new Set(this.availableFoldersForMove.map(f => f.name));
      const flatten = (nodes, depth = 0) => {
        const result = [];
        for (const node of nodes) {
          if (node.fullName === this.selectedFolder) continue;
          if (allowedNames.has(node.fullName)) {
            result.push({ name: node.name, fullName: node.fullName, depth });
          }
          if (node.children && node.children.length) {
            result.push(...flatten(node.children, depth + 1));
          }
        }
        return result;
      };
      return flatten(this.treeFolders);
    },

    selectedFolderDisplayName() {
      if (!this.selectedFolder) return 'Aucun dossier';
      const flatten = (nodes) => {
        const result = [];
        for (const node of nodes) {
          result.push(node);
          if (node.children) result.push(...flatten(node.children));
        }
        return result;
      };
      const found = flatten(this.treeFolders).find(n => n.fullName === this.selectedFolder);
      return found ? found.name : this.selectedFolder;
    },
  },

  methods: {
    showConfirm(message) {
      return new Promise((resolve) => {
        this.confirmModal.message = message;
        this.confirmModal.resolve = resolve;
        this.confirmModal.visible = true;
      });
    },

    resolveConfirm(value) {
      this.confirmModal.visible = false;
      if (this.confirmModal.resolve) {
        this.confirmModal.resolve(value);
        this.confirmModal.resolve = null;
      }
    },

    async deleteSelectedMails(permanent = false) {
      if (this.selectedCount === 0) {
        await this.$store.dispatch("notify", {
          level: "warning",
          text: "Aucun email sélectionné",
          seconds: 3
        });
        return;
      }

      const isDraftFolder = this.isDraft(this.selectedFolder);
      const confirmMsg = isDraftFolder
          ? `Voulez-vous vraiment supprimer ${this.selectedCount} brouillon(s) ?`
          : permanent
              ? `Voulez-vous vraiment supprimer définitivement ${this.selectedCount} email(s) ?`
              : `Voulez-vous déplacer ${this.selectedCount} email(s) vers la corbeille ?`;

      if (!await this.showConfirm(confirmMsg)) return;

      try {
        const uids = this.selectedMails.map(m => m.uid);

        const res = await gqlMutation(DELETE_MAIL, {
          config_id: this.config.uuid,
          folder: this.selectedFolder,
          uids,
          permanent: permanent || isDraftFolder,
        });

        const data = res.body.data.delete_mails;

        if (data.ok) {
          await this.$store.dispatch("notify", {
            level: "success",
            text: `${data.deleted_count} ${isDraftFolder ? 'brouillon(s)' : 'email(s)'} supprimé(s)`,
            seconds: 3
          });

          this.selectedMails = [];

          if (this.mail && uids.includes(this.mail.uid)) {
            this.mail = null;
            this.selectedMailId = null;
          }

          await this.fetchMails();
        }
      } catch (error) {
        console.error("Erreur suppression:", error);
        await this.$store.dispatch("notify", {
          level: "danger",
          text: "Erreur lors de la suppression",
          seconds: 5
        });
      }
    },

    // ===== NOUVELLE MÉTHODE : Déplacer les emails sélectionnés =====
    async moveSelectedMails(destinationFolder) {
      if (this.selectedCount === 0) {
        await this.$store.dispatch("notify", {
          level: "warning",
          text: "Aucun email sélectionné",
          seconds: 3
        });
        return;
      }

      try {
        const uids = this.selectedMails.map(m => m.uid);

        const res = await gqlMutation(MOVE_MAIL, {
          config_id: this.config.uuid,
          source_folder: this.selectedFolder,
          destination_folder: destinationFolder,
          uids
        });

        const data = res.body.data.move_mails;

        if (data.ok) {
          await this.$store.dispatch("notify", {
            level: "success",
            text: `${data.moved_count} email(s) déplacé(s)`,
            seconds: 3
          });

          // Réinitialiser la sélection
          this.selectedMails = [];

          // Si l'email actuellement affiché est déplacé
          if (this.mail && uids.includes(this.mail.uid)) {
            this.mail = null;
            this.selectedMailId = null;
          }

          // Rafraîchir la liste
          await this.fetchMails();
        }
      } catch (error) {
        console.error("Erreur déplacement:", error);
        await this.$store.dispatch("notify", {
          level: "danger",
          text: "Erreur lors du déplacement des emails",
          seconds: 5
        });
      }
    },

    toggleSelectAll() {
      if (this.selectedMails.length === this.mails.length) {
        // Tout désélectionner
        this.selectedMails = [];
      } else {
        // Tout sélectionner
        this.selectedMails = [...this.mails];
      }
    },

    async generateReplies(assistant_uid) {
      const payload = {
        assistant_id: assistant_uid,
        user_mail_config_id: this.config.uuid,
        additional_info: this.replyContext.trim() || undefined,
        emails_contexts: []
      }

      const results = await Promise.all(
          this.selectedMails.map(mail => gqlQuery(WEBMAIL_MAIL, {
            config_id: this.config.uuid,
            folder: this.selectedFolder,
            uid: mail.uid,
          }))
      )
      payload.emails_contexts = results.map(res => {
        const fullMail = res.body.data.webmail_get_mail
        return {
          subject: fullMail.subject,
          to: fullMail.from_,
          to_original: fullMail.to,
          date: fullMail.date,
          body: fullMail.text,
          body_html: fullMail.html,
        }
      })

      await this.$http.post(settings.backendUrl + "/admin/webmail/generate_draft_emails",
          JSON.stringify(payload),
          {
            headers: {
              "Content-Type": "application/json"
            }
          }
      )
      await this.$store.dispatch("notify", {
        level: "info",
        text: "Génération des réponses en cours…",
        seconds: 3
      })
      this.selectedMails = [];
    },

    isSelected(uid) {
      return this.selectedMails.some(m => m.uid === uid);
    },

    toggleSelection(mail) {
      const index = this.selectedMails.findIndex(m => m.uid === mail.uid);
      if (index > -1) {
        this.selectedMails.splice(index, 1);
      } else {
        this.selectedMails.push(mail);
      }
    },

    toggleFolder(folder) {
      this.$set(
          this.openFolders,
          folder.fullName,
          !this.openFolders[folder.fullName]
      );
    },

    async aiReply(uid) {
      this.showReply = true;
      await this.$nextTick();
      const compose = this.$refs.composeRef;
      if (!compose) return;
      await compose.generateAnwser(uid);
    },

    async refreshCurrentFolder() {
      if (!this.selectedFolder) return;
      await this.fetchMails();
      this.showReply = false;
      this.writeEmail = false;
      this.mail = null;
      this.selectedMailId = null;
      this.composeDirty = false;
    },

    async selectFolder(folder) {
      if (this.composeDirty) {
        return this.askDraftSave(() => this.selectFolder(folder));
      }

      this.selectedFolder = folder;
      this.mail = null;
      this.selectedMailId = null;
      this.selectedMails = [];
      await this.fetchMails();
    },

    async fetchMails() {
      this.loadingMails = true;
      const r = await gqlQuery(WEBMAIL_MAIL_BOX, {
        config_id: this.config.uuid,
        folder: this.selectedFolder,
        limit: 50,
        offset: 0
      });
      this.mails = r.body.data.webmail_get_mail_box || [];
      if (this.isDraft(this.selectedFolder)) {
        this.mails = this.mails.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
      }
      this.loadingMails = false;
    },

    async openDraft(mail) {
      const res = await gqlQuery(WEBMAIL_MAIL, {
        config_id: this.config.uuid,
        folder: this.selectedFolder,
        uid: mail.uid,
      });
      const imapMail = res.body.data.webmail_get_mail;

      this.mail = null;
      this.showReply = false;
      this.writeEmail = true;

      await this.$nextTick();

      const compose = this.$refs.composeWriteRef;
      if (!compose) {
        console.warn("Compose write non monté");
        return;
      }

      compose.loadDraft({
        uid: mail.uid,
        subject: imapMail.subject,
        to: Array.isArray(imapMail.to) ? imapMail.to.join(',') : (imapMail.to || ''),
        cc: Array.isArray(imapMail.cc) ? imapMail.cc.join(',') : (imapMail.cc || ''),
        bcc: Array.isArray(imapMail.bcc) ? imapMail.bcc.join(',') : (imapMail.bcc || ''),
        body_html: imapMail.html,
        body_text: imapMail.text,
      });
    },

    openMail(mail) {
      if (this.composeDirty) {
        return this.askDraftSave(() => this.openMail(mail));
      }
      if (this.isDraft(this.selectedFolder)) {
        this.openDraft(mail);
      } else {
        this.loadMail(mail.uid);
      }
    },

    async askDraftSave(nextAction) {
      this.pendingNavigation = nextAction;

      if (await this.showConfirm("Vous avez des modifications non enregistrées. Voulez-vous les enregistrer dans les brouillons ?")) {
        this.loading = true;
        const compose = this.$refs.composeRef || this.$refs.composeWriteRef;
        try {
          if (compose) await compose.saveDraft();
        } finally {
          this.composeDirty = false;
          this.loading = false;
        }
        nextAction();
      } else {
        this.composeDirty = false;
        nextAction();
      }
    },

    async loadMail(uid) {
      this.loadingMailView = true;
      let folder = this.selectedFolder;

      const res = await gqlQuery(WEBMAIL_MAIL, {
        config_id: this.config.uuid,
        folder,
        uid
      });

      this.mail = res.body.data.webmail_get_mail;
      this.showReply = false;
      this.loadingMailView = false;
    },

    toggleReply() {
      this.showReply = !this.showReply;
    },

    toggleWrite() {
      this.showReply = false;
      this.writeEmail = true;
      this.mail = null;
    },

    formatDate(str) {
      if (!str) return "";
      return new Date(str).toLocaleString("fr-FR");
    },

    isDraft(folder) {
      const folderObject = this.folders.find(f => f.name === folder);
      return folderObject && folderObject.isDraftFolder;
    },

    async deleteCurrentMail() {
      if (!await this.showConfirm("Voulez-vous déplacer cet email vers la corbeille ?")) return;

      const res = await gqlMutation(DELETE_MAIL, {
        config_id: this.config.uuid,
        folder: this.selectedFolder,
        uids: [this.mail.uid],
        permanent: false
      });

      const data = res.body.data.delete_mails;
      if (data.ok) {
        this.mail = null;
        this.selectedMailId = null;
        await this.fetchMails();
        await this.$store.dispatch("notify", {
          level: "success",
          text: "Email déplacé vers la corbeille.",
          seconds: 3
        });
      } else {
        await this.$store.dispatch("notify", {
          level: "danger",
          text: "Erreur lors de la suppression.",
          seconds: 5
        });
      }
    },

    async deleteDraft(uid) {
      if (!await this.showConfirm("Voulez-vous vraiment supprimer ce brouillon ?")) return;

      try {
        const res = await gqlMutation(DELETE_MAIL, {
          config_id: this.config.uuid,
          folder: this.selectedFolder,
          uids: [uid],
          permanent: true,
        });

        if (res.body.data.delete_mails.ok) {
          this.mail = null;
          this.selectedMailId = null;
          await this.fetchMails();
          await this.$store.dispatch("notify", {level: "success", text: "Brouillon supprimé.", seconds: 3});
        }
      } catch (error) {
        await this.$store.dispatch("notify", {
          level: "danger",
          text: "Erreur lors de la suppression du brouillon.",
          seconds: 5
        });
      }
    },

    async onDraftDeleted(uid) {
      try {
        if (uid) {
          await gqlMutation(DELETE_MAIL, {
            config_id: this.config.uuid,
            folder: this.selectedFolder,
            uids: [uid],
            permanent: true,
          });
        }
        this.writeEmail = false;
        this.mail = null;
        this.composeDirty = false;
        await this.fetchMails();
        await this.$store.dispatch("notify", {level: "success", text: "Brouillon supprimé.", seconds: 3});
      } catch (error) {
        await this.$store.dispatch("notify", {
          level: "danger",
          text: "Erreur lors de la suppression du brouillon.",
          seconds: 5
        });
      }
    }

  },

  async beforeRouteLeave(to, from, next) {
    if (!this.composeDirty) {
      next();
      return;
    }
    const save = await this.showConfirm("Vous avez des modifications non enregistrées. Voulez-vous les enregistrer dans les brouillons ?");
    if (save) {
      const compose = this.$refs.composeRef || this.$refs.composeWriteRef;
      if (compose) await compose.saveDraft();
    }
    this.composeDirty = false;
    next();
  },

  async mounted() {
    this.$djangows.onmessage(async (data) => {
      const json = JSON.parse(data.data)

      if (
          json.signal_type === "custom" &&
          json.message === "generation_finished" &&
          this.config &&
          json.obj.user_mail_config_id === this.config.uuid
      ) {
        this.$store.dispatch("notify", {
          level: "success",
          text: `Génération terminée (${json.obj.total} brouillons)`,
          seconds: 4
        })

        this.isGenerating = false

        const draftsFolder = this.folders.find(f => f.isDraftFolder)
        if (draftsFolder) {
          await this.selectFolder(draftsFolder.name)
        }
      }
    })
    const id = this.$route.params.id;

    const [configRes, estabRes] = await Promise.all([
      gqlQuery(USER_MAIL_CONFIG_LIST, {consultant_uid: this.$store.state.profile.data.uid}),
      gqlQuery(ESTABLISHMENT_SET, {consultant_uid: this.$store.state.profile.data.uid}),
    ]);

    this.config = (configRes.body.data.user_mail_config_list || []).find(x => x.uuid == id);

    const establishmentUids = (estabRes.body.data.establishment_set || []).map(e => e.uid);
    if (establishmentUids.length > 0) {
      const results = (await gqlQuery(ASSISTANT, {tags: ["mail"], bool_compute_elos: false})).body.data.assistants || []
      const establishmentUidSet = new Set(establishmentUids);
      const seen = new Set();
      this.assistantset = results
          .filter(a => a.is_active && !seen.has(a.uid) && seen.add(a.uid)
              && a.affectation_set.some(aff => establishmentUidSet.has(aff.establishment.uid)));
    }

    if (this.config) {
      const res = await gqlQuery(WEBMAIL_FOLDERS, {config_id: id});
      const rawFolders = res.body.data.webmail_get_folders || [];
      this.folders = rawFolders.map(f => ({
        ...f,
        isDraftFolder: (Array.isArray(f.flags) && f.flags.some(flag => flag.toLowerCase() === '\\drafts'))
            || ['brouillons', 'drafts', 'draft'].includes((f.name || '').split(/[/.]/).pop().toLowerCase()),
      }));
    }

    if (this.config && !this.folders.some(f => f.isDraftFolder) && !this.config.server.draft_folder) {
      this.folders.unshift({
        name: "Brouillons",
        flags: [],
        isDraftFolder: true,
      });
    }

    if (this.treeFolders.length > 0) {
      await this.selectFolder(this.treeFolders[0].fullName);
    }
    this.loading = false;

  }
}
</script>

<template>
  <div class="webmail-3col">
    <!-- COLONNE 1 : FOLDERS -->
    <aside class="col-folders">
      <div class="d-flex pt-3 pb-2 gap-2 align-items-center">
        <button class="btn btn-primary btn-icon-only btn-sm" @click="$router.push('/webmail?no_redirect=true')">
          <i class="fa-solid fa-arrow-left"></i>
        </button>
        <h5 class="">Dossiers</h5>
      </div>
      <div class="folder-scroll">
        <ul class="folder-tree">
          <template v-for="folder in treeFolders">
            <folder-node
                :key="folder.fullName"
                :node="folder"
                :selected-folder="selectedFolder"
                @select-folder="selectFolder"
                @toggle="toggleFolder"
            />
          </template>
        </ul>
        <p v-if="!loading && treeFolders.length === 0" class="text-muted text-center small mt-3">
          Aucun dossier trouvé
        </p>
      </div>
    </aside>

    <!-- COLONNE 2 : MAIL LIST -->
    <section class="col-mails">
      <div class="maillist-header p-3 d-flex justify-content-between flex-column gap-2">
        <h5 class="m-0">{{ selectedFolderDisplayName }}
          <span v-if="selectedCount > 0" class="badge bg-primary me-auto">
            {{ selectedCount }} sélectionné(s)
          </span></h5>

        <!-- Barre d'actions principales -->
        <div class="d-flex flex-wrap btn-group">
          <button class="btn btn-sm btn-outline-primary" @click="fetchMails" :disabled="!selectedFolder">
            <i class="fa-solid fa-rotate"></i> Rafraîchir
          </button>
          <button class="btn btn-sm btn-primary" @click="toggleWrite">
            <i class="fa-solid fa-pen"></i> Ecrire
          </button>
          <button
              class="btn btn-sm btn-outline-secondary"
              @click="toggleSelectAll"
              :disabled="mails.length === 0"
          >
            <i class="fa-solid fa-check-double"></i>
            {{ selectedCount === mails.length ? 'Tout désélectionner' : 'Tout sélectionner' }}
          </button>

        </div>
        <!-- Barre d'actions de sélection -->
        <div v-if="selectedCount > 0" class="d-flex align-items-center gap-2 flex-wrap">
          <div class="btn-group" v-if="assistantset.length > 0">
            <button
                class="btn btn-sm btn-outline-success dropdown-toggle"
                data-bs-toggle="dropdown"
                :disabled="selectedCount === 0"
            >
              <i class="fa-solid fa-wand-magic-sparkles"></i> Générer des réponses
            </button>
            <div class="dropdown-menu">
              <a
                  v-for="assistant in assistantset"
                  :key="assistant.uid"
                  class="dropdown-item"
                  @click.prevent="generateReplies(assistant.uid)"
              >
                {{ assistant.name }}
              </a>
            </div>
          </div>
          <input
              v-if="assistantset.length > 0"
              v-model="replyContext"
              type="text"
              class="form-control form-control-sm reply-context-input"
              placeholder="Contexte pour la génération IA (optionnel)…"
          />
          <!-- Déplacer -->
          <div class="btn-group" v-if="!isDraft(selectedFolder)">
            <button
                class="btn btn-sm btn-outline-info dropdown-toggle"
                data-bs-toggle="dropdown"
            >
              <i class="fa-solid fa-folder-open"></i> Déplacer
            </button>
            <div class="dropdown-menu">
              <a
                  v-for="folder in flatFoldersForMove"
                  :key="folder.fullName"
                  class="dropdown-item"
                  :style="{ paddingLeft: (12 + folder.depth * 16) + 'px' }"
                  @click.prevent="moveSelectedMails(folder.fullName)"
              >
                {{ folder.name }}
              </a>
            </div>
          </div>

          <!-- Supprimer -->
          <button
              class="btn btn-sm btn-outline-danger"
              @click="deleteSelectedMails(false)"
          >
            <i class="fa-solid fa-trash"></i> Corbeille
          </button>

          <!-- Supprimer définitivement (si dans Trash) -->
          <button
              v-if="selectedFolder && selectedFolder.toLowerCase().includes('trash')"
              class="btn btn-sm btn-danger"
              @click="deleteSelectedMails(true)"
          >
            <i class="fa-solid fa-trash-alt"></i> Supprimer définitivement
          </button>
        </div>
      </div>

      <div class="maillist-scroll">
        <div v-if="loadingMails" class="loader-mailing">
          <div class="spinner-border"></div>
        </div>

        <table v-else class="table mails-table">
          <tbody>
          <tr
              v-for="mail in mails"
              :key="mail.uid"
              :class="{
                selected: mail.uid === selectedMailId,
                'mail-selected': isSelected(mail.uid)
              }"
              @mouseenter="hoveredMailUid = mail.uid"
              @mouseleave="hoveredMailUid = null"
          >
            <!-- Checkbox -->
            <td
                class="checkbox-cell"
                @click.stop
            >
              <input
                  type="checkbox"
                  :checked="isSelected(mail.uid)"
                  @change="toggleSelection(mail)"
                  class="form-check-input"
              >
            </td>

            <!-- Contenu mail -->
            <td @click="openMail(mail)" class="mail-content">
              <div class="mail-item-sub">{{ mail.subject }}</div>
              <div class="mail-item-from">{{ mail.from_ }}</div>
              <div class="mail-item-date">{{ formatDate(mail.date) }}</div>
            </td>
          </tr>
          </tbody>
        </table>

        <div v-if="!loadingMails && mails.length === 0" class="empty-list">
          <span class="ms-2">
            Aucun mail dans ce dossier.
          </span>
        </div>
      </div>
    </section>

    <!-- COLONNE 3 : MAIL VIEW / COMPOSE -->
    <section class="col-view">
      <div v-if="loadingMailView" class="col-center h-100">
        <div class="spinner-border"></div>
      </div>

      <div v-else-if="mail" class="mailview-scroll">

        <!-- Header -->
        <div class="mailview-header flex-column gap-2" v-if="!showReply">

          <!-- Sujet -->
          <div class="d-flex justify-content-between align-items-start">
            <h5 class="mb-0">{{ mail.subject || '(Sans sujet)' }}</h5>

            <div class="btn-group">
              <button class="btn btn-sm btn-outline-primary" @click="toggleReply">
                Répondre
              </button>

              <div class="btn-group" v-if="assistantset.length > 0">
                <button
                    class="btn btn-sm btn-outline-success dropdown-toggle"
                    data-bs-toggle="dropdown"
                >
                  Réponse IA
                </button>
                <div class="dropdown-menu">
                  <a
                      v-for="assistant in assistantset"
                      :key="assistant.uid"
                      class="dropdown-item"
                      @click.prevent="aiReply(assistant.uid)"
                  >
                    {{ assistant.name }}
                  </a>
                </div>
              </div>

              <button
                  v-if="isDraft(selectedFolder)"
                  class="btn btn-sm btn-danger"
                  @click="deleteDraft(mail.uid)"
              >
                Supprimer
              </button>

              <button
                  v-if="!isDraft(selectedFolder)"
                  class="btn btn-sm btn-outline-danger"
                  @click="deleteCurrentMail"
              >
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </div>

          <!-- Infos mail -->
          <div class="small text-muted">
            <div>
              <strong>{{ mail.from_ }}</strong>
            </div>
            <div>
              À : {{ Array.isArray(mail.to) ? mail.to.join(', ') : mail.to }}
            </div>
            <div v-if="mail.cc">
              cc : {{ Array.isArray(mail.cc) ? mail.cc.join(', ') : mail.cc }}
            </div>
            <div v-if="mail.bcc">
              cci : {{ Array.isArray(mail.bcc) ? mail.bcc.join(', ') : mail.bcc }}
            </div>
            <div>
              {{ formatDate(mail.date) }}
            </div>
          </div>

        </div>
        <div
            v-if="mail.attachments && mail.attachments.length > 0"
            class="attachments-zone border-bottom px-3 py-2"
        >
          <div class="d-flex gap-2 flex-wrap">
            <div
                v-for="att in mail.attachments"
                :key="att.filename"
                class="border rounded p-2 d-flex align-items-center gap-2 attachment-item"
            >
              <i class="fa-solid fa-file-pdf text-danger"></i>

              <div class="small">
                <div class="fw-bold">{{ att.filename }}</div>
                <div class="text-muted">{{ att.size }} o</div>
              </div>

              <a
                  class="btn btn-sm btn-outline-primary ms-auto"
                  :href="'data:' + att.content_type + ';base64,' + att.payload"
                  :download="att.filename"
                  @click.stop
              >
                Télécharger
              </a>

            </div>
          </div>
        </div>

        <!-- Reply form -->
        <div v-if="showReply" class="reply-zone">
          <WebmailCompose
              ref="composeRef"
              :to_answer="mail.from_"
              :cc_original="mail.cc"
              :bcc_original="mail.bcc"
              :to_original="mail.to"
              :subject="`Re: ${mail.subject}`"
              :date="mail.date"
              :body-from="mail.text"
              :body-html-from="mail.html"
              :assistantset="assistantset"
              @dirty="composeDirty = true"
              @clean="composeDirty = false"
              @sent="refreshCurrentFolder"
              @draft-deleted="onDraftDeleted"
          />
        </div>

        <!-- Full mail content -->
        <div
            class="mailview-body"
            v-html="mail.html || mail.text"
        ></div>
      </div>
      <div v-else-if="writeEmail" class="reply-zone">
        <WebmailCompose
            ref="composeWriteRef"
            subject=""
            :assistantset="assistantset"
            @dirty="composeDirty = true"
            @clean="composeDirty = false"
            @sent="refreshCurrentFolder"
            @draft-deleted="onDraftDeleted"
        />
      </div>
      <!-- Placeholder -->
      <div v-else class="col-center placeholder">
        <i class="fa-solid fa-envelope fa-beat fa-2xl"></i>
        <p class="h1">Aucun email sélectionné</p>
      </div>
    </section>

    <!-- Confirm modal -->
    <transition name="modal-fade">
      <div v-if="confirmModal.visible" class="confirm-backdrop" @click.self="resolveConfirm(false)">
        <div class="confirm-dialog">
          <div class="confirm-body">
            <i class="fa-solid fa-triangle-exclamation text-warning me-2"></i>
            {{ confirmModal.message }}
          </div>
          <div class="confirm-footer">
            <button class="btn btn-sm btn-outline-secondary" @click="resolveConfirm(false)">Annuler</button>
            <button class="btn btn-sm btn-primary" @click="resolveConfirm(true)">Confirmer</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped lang="scss">
.reply-context-input {
  min-width: 220px;
  max-width: 360px;
}

.webmail-3col {
  display: flex;
  height: calc(100vh - 50px);
  overflow: hidden;
  background: #fff;
  margin-top: -30px;
}

/* --- COLONNE 1 : FOLDERS --- */

.col-folders {
  flex: 0 0 200px;
  border-right: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.folder-scroll {
  flex: 1;
  overflow-y: auto;
}

.folder-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.folder-list li {
  padding: 8px 12px;
  cursor: pointer;
  transition: 0.2s;
  border-radius: 4px;
}

.folder-list li:hover {
  background: #eef2f2;
}

.folder-list li.active {
  background: #7c9c9c;
  color: white;
}

/* --- COLONNE 2 : MAILS LIST --- */

.col-mails {
  flex: 0 0 500px;
  border-right: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.maillist-header {
  background: #f8f8f8;
  border-bottom: 1px solid #ddd;
}

.maillist-scroll {
  flex: 1;
  overflow-y: auto;
}

.mails-table {
  margin-bottom: 0;

  tr {
    transition: 0.15s;

    &:hover {
      background: #f3f3f3;
    }

    &.selected {
      background: #dbe5e5;
    }

    &.mail-selected {
      background: #e3f2fd;
    }
  }

  .checkbox-cell {
    width: 30px !important;
    vertical-align: middle;
    cursor: default;
    padding: 12px 8px;
  }

  .mail-content {
    cursor: pointer;
    padding: 12px;
  }
}

.mail-item-from {
  font-size: 0.9rem;
  color: #666;
}

.mail-item-sub {
  font-weight: bold;
}

.mail-item-date {
  font-size: 0.75rem;
  color: #999;
}

/* --- COLONNE 3 : MAIL VIEW --- */

.col-view {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mailview-scroll {
  flex: 1;
  overflow-y: auto;
}

.mailview-header {
  padding: 1rem;
  background: #fafafa;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
}

.reply-zone {
  padding: 1rem;
  border-bottom: 1px solid #ddd;
  background: #fff;
}

.mailview-body {
  padding: 1.2rem;
}

/* --- ÉTUDE DU CENTRAGE --- */

.col-center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #999;
  gap: 25px;
}

/* --- DIVERS --- */

.loader-mailing {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.attachments-zone {
  background: #fafafa;
}

.attachment-item {
  min-width: 220px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f1f1f1;
  }
}

.empty-list {
  padding: 2rem;
  text-align: center;
  color: #999;
}

.confirm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.confirm-dialog {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  min-width: 320px;
  max-width: 480px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.confirm-body {
  font-size: 0.95rem;
  color: #333;
  line-height: 1.5;
}

.confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.15s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

</style>
